import { useState, useEffect, memo, useCallback, useMemo } from "react";
import { CSVLink } from "react-csv";
import AnimacionCarga from "../animacion_de_carga";
import TablaGenerica from "../tablas";
import { obtenerCenso } from "../../../functions/consultas";
import BarraSuperior from "../barra_superior";
import BotonSOS from "../sos/boton_sos";
import "bootstrap-icons/font/bootstrap-icons.css";


const TIPOS_InformesCenso = {
    residentes: {
        titulo: "Residentes",
        campos: [
            "nombre_residente",
            "cedula_residente",
            "telefono_residente",
            "tipo_residente",
            "correo_residente",
            "genero_residente"
        ],
        columnas: [
            { key: "vivienda", label: "Vivienda" },
            { key: "nombre_residente", label: "Nombre" },
            { key: "cedula_residente", label: "Cédula" },
            { key: "telefono_residente", label: "Teléfono" },
            { key: "tipo_residente", label: "Tipo" },
            { key: "correo_residente", label: "Email" },
            { key: "genero_residente", label: "Género" }
        ]
    },

    vehiculos: {
        titulo: "Vehículos",
        campos: ["marca_carro", "color_carro", "placa"],
        columnas: [
            { key: "vivienda", label: "Vivienda" },
            { key: "marca_carro", label: "Marca" },
            { key: "color_carro", label: "Color" },
            { key: "placa", label: "Placa" }
        ]
    },

    mascotas: {
        titulo: "Mascotas",
        campos: ["nombre_mascota", "tipo_mascota"],
        columnas: [
            { key: "vivienda", label: "Vivienda" },
            { key: "nombre_mascota", label: "Nombre" },
            { key: "tipo_mascota", label: "Tipo" }
        ]
    },

    autorizados: {
        titulo: "Personas Autorizadas",
        campos: ["nombre_autorizado", "cedula_autorizado", "tipo_relacion_autorizado"],
        columnas: [
            { key: "vivienda", label: "Vivienda" },
            { key: "nombre_autorizado", label: "Nombre" },
            { key: "cedula_autorizado", label: "Cédula" },
            { key: "tipo_relacion_autorizado", label: "Relación" }
        ]
    },

    espacios: {
        titulo: "Espacios",
        campos: ["espacio_tipo", "espacio_identificador", "espacio_coeficiente"],
        columnas: [
            { key: "vivienda", label: "Vivienda" },
            { key: "espacio_tipo", label: "Tipo" },
            { key: "espacio_identificador", label: "Identificador" },
            { key: "espacio_coeficiente", label: "Coeficiente" }
        ]
    },

    contactos_emergencia: {
        titulo: "Contactos de Emergencia",
        campos: [
            "nombre_contacto_emergencia",
            "telefono_contacto_emergencia",
            "descripcion_contacto_emergencia"
        ],
        columnas: [
            { key: "vivienda", label: "Vivienda" },
            { key: "nombre_contacto_emergencia", label: "Nombre" },
            { key: "telefono_contacto_emergencia", label: "Teléfono" },
            { key: "descripcion_contacto_emergencia", label: "Descripción" }
        ]
    }
};



const normalizarDatos = (vivienda, camposBase) => {
    const registros = [];
    let maxIndex = 0;
    Object.keys(vivienda).forEach(key => {
        const match = key.match(/_(\d+)$/);
        if (match) {
            const index = parseInt(match[1], 10);
            if (index > maxIndex) maxIndex = index;
        }
    });
    for (let i = 1; i <= maxIndex; i++) {
        const registro = { vivienda: vivienda.direccion || "Sin dirección" };

        let tieneDatos = false;

        camposBase.forEach(base => {
            const key = `${base}_${i}`;
            const valor = vivienda[key];
            if (valor !== undefined && valor !== null && valor !== "") {
                tieneDatos = true;
            }
            registro[base] = valor ?? "N/A";
        });
        if (tieneDatos) registros.push(registro);
    }

    return registros;
};


const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const InformesCenso = memo(() => {
    const [datos, setDatos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [tipoSeleccionado, setTipoSeleccionado] = useState('residentes');

    const capturarDatos = useCallback(async () => {
        setCargando(true);
        try {
            const data = await obtenerCenso();
            setDatos(data ? Object.values(data) : []);
        } catch (error) {
            console.error('Error al obtener datos:', error);
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        capturarDatos();
    }, [capturarDatos]);

    const datosProcesados = useMemo(() => {
        const config = TIPOS_InformesCenso[tipoSeleccionado];
        const todosRegistros = datos.flatMap(vivienda =>
            normalizarDatos(vivienda, config.campos)
        );
        return todosRegistros.sort((a, b) => a.vivienda.localeCompare(b.vivienda));
    }, [datos, tipoSeleccionado]);

    const config = TIPOS_InformesCenso[tipoSeleccionado];

    if (cargando) return <AnimacionCarga />;

    return (
        <>
            <BarraSuperior />

            {}
            <div style={{
                padding: '15px 20px',
                background: '#f5f5f5',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
            }}>
                <label style={{ fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    Tipo de Informe:
                </label>
                <select
                    value={tipoSeleccionado}
                    onChange={(e) => setTipoSeleccionado(e.target.value)}
                    style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        minWidth: '250px',
                        cursor: 'pointer'
                    }}
                >
                    {Object.entries(TIPOS_InformesCenso).map(([key, value]) => (
                        <option key={key} value={key}>{value.titulo}</option>
                    ))}
                </select>
                <span style={{ fontSize: '13px', color: '#666', marginLeft: 'auto' }}>
                    Total de registros: <strong>{datosProcesados.length}</strong>
                </span>
            </div>

            {}
            <TablaGenerica
                datos={datosProcesados}
                titulos={config.columnas.map((c) => c.label.toUpperCase())}
                campos={config.columnas.map((c) => c.key)}
                elementosPorPagina={300}
                columnaOrdenInicial={0}
                direccionOrdenInicial="asc"
                mostrarBarraSuperior={false}
                contenidoExtra={
                  <>
                    <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
                    <CSVLink data={datosProcesados} headers={config.columnas} filename={`informe_${tipoSeleccionado}.csv`} separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
                  </>
                }
            />
            <BotonSOS />
        </>
    );
});

export default InformesCenso;
